import { useState, useEffect, useCallback } from 'react';
import { quotesAPI, catalogAPI, pricingAPI } from '../../services/api';
import { fmt, ALERT_META } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';
import SearchSelect from '../common/SearchSelect';
import AluElevationPreview from './AluElevationPreview';
import * as ALU from './aluCatalog';
import { catalogCategory, catalogBlurb, CATALOG_CATEGORIES } from '../../utils/aluCategories';
import { resolveSeries, tracksFromConfig } from '../../utils/aluSeriesResolver';
import { validateAluLine, describeViolation } from '../../utils/aluConstraints';
import { sendWithCrossFamilyAck } from '../../utils/crossFamily';
import toast from 'react-hot-toast';

// CatalogField — SearchSelect sobre lista curada de strings, con Custom -> texto libre.
function CatalogField({ label, options, value, onChange, allowCustom = true, hint, auto = false }) {
  const inList = options.includes(value);
  const [custom, setCustom] = useState(!!value && !inList);
  const opts = allowCustom && !options.includes('Custom') ? [...options, 'Custom'] : options;
  return (
    <div className="form-group">
      <label className="form-label">
        {label}
        {auto && (
          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600,
                         color: 'var(--gold, #b8995f)', letterSpacing: '.04em' }}>
            AUTO
          </span>
        )}
      </label>
      <SearchSelect
        options={opts}
        value={custom ? 'Custom' : value}
        onChange={(v) => {
          if (v === 'Custom') { setCustom(true); onChange(''); }
          else { setCustom(false); onChange(v); }
        }}
        getValue={s => s}
        getLabel={s => s}
        placeholder="Select..."
      />
      {custom && (
        <input className="form-input" style={{ marginTop: 6 }}
          placeholder="Enter custom value..." autoFocus
          value={value} onChange={e => onChange(e.target.value)} />
      )}
      {hint && <div className="form-hint">{hint}</div>}
    </div>
  );
}

// grilleIcon — mini SVG preview per grille pattern (for the selector tiles)
function grilleIcon(type) {
  const W = 34, H = 44, x = 3, y = 3, w = W - 6, h = H - 6, M = '#1c1d21';
  const L = (a, b, c, d) => `<line x1="${a}" y1="${b}" x2="${c}" y2="${d}" stroke="${M}" stroke-width="1.2"/>`;
  const t = String(type).toLowerCase();
  let g = '', clip = '';
  if (t.includes('colonial')) { for (let i = 1; i < 3; i++) g += L(x + w * i / 3, y, x + w * i / 3, y + h); for (let j = 1; j < 3; j++) g += L(x, y + h * j / 3, x + w, y + h * j / 3); }
  else if (t.includes('prairie')) { const f = 0.2; g += L(x + w * f, y, x + w * f, y + h) + L(x + w * (1 - f), y, x + w * (1 - f), y + h) + L(x, y + h * f, x + w, y + h * f) + L(x, y + h * (1 - f), x + w, y + h * (1 - f)); }
  else if (t.includes('perimeter')) { const f = 0.18; g += `<rect x="${x + w * f}" y="${y + h * f}" width="${w * (1 - 2 * f)}" height="${h * (1 - 2 * f)}" fill="none" stroke="${M}" stroke-width="1.2"/>`; }
  else if (t.includes('diamond')) { clip = `<clipPath id="dic"><rect x="${x}" y="${y}" width="${w}" height="${h}"/></clipPath>`; const s = w / 2; let dd = ''; for (let k = -2; k <= 4; k++) { const ox = x + k * s; dd += `<line x1="${ox}" y1="${y}" x2="${ox + h}" y2="${y + h}" stroke="${M}" stroke-width="1"/>` + `<line x1="${ox}" y1="${y + h}" x2="${ox + h}" y2="${y}" stroke="${M}" stroke-width="1"/>`; } g = `<g clip-path="url(#dic)">${dd}</g>`; }
  else if (t.includes('top')) { const ty = y + h * 0.3; g += L(x, ty, x + w, ty) + L(x + w / 3, y, x + w / 3, ty) + L(x + 2 * w / 3, y, x + 2 * w / 3, ty); }
  else if (t.includes('custom')) { g = `<text x="${x + w / 2}" y="${y + h / 2 + 5}" text-anchor="middle" font-size="15" fill="#64748b">?</text>`; }
  return `<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"><defs>${clip}</defs><rect x="${x}" y="${y}" width="${w}" height="${h}" rx="1.5" fill="#eef3f7" stroke="#9aa0a8" stroke-width="1"/>${g}</svg>`;
}

// GrillePatternSelect — icon tiles. Sets grilles_pattern to the type keyword.
function GrillePatternSelect({ value, onType }) {
  const TYPES = ['Colonial', 'Prairie', 'Perimeter', 'Diamond', 'Top Row'];
  const v = String(value || '');
  const known = TYPES.find(t => t.toLowerCase() === v.toLowerCase());
  const selected = known || (v ? 'Custom' : '');
  const pick = (label) => onType(label === 'Custom' ? 'Custom' : label);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {[...TYPES, 'Custom'].map(label => {
        const active = selected === label;
        return (
          <button type="button" key={label} onClick={() => pick(label)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
              padding: '6px 6px', cursor: 'pointer', borderRadius: 8, width: 60,
              border: active ? '2px solid #2563eb' : '1px solid #d1d5db',
              background: active ? '#eff6ff' : '#fff'
            }}>
            <span style={{ lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: grilleIcon(label) }} />
            <span style={{ fontSize: 11, color: '#334155' }}>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function AluLineModal({ quoteId, editData, installer = 'buiria', crossFamilyAck = false, onClose, onSaved }) {
  const { isManager } = useAuth();

  const [models, setModels]     = useState([]);
  const [typeSeries, setTypeSeries] = useState({});
  const [buiria, setBuiria]     = useState([]);
  const [scales, setScales]     = useState([]);
  const [catalog, setCatalog]   = useState([]);
  const [constraints, setConstraints] = useState([]);
  const [catSel, setCatSel]     = useState(editData?.alu_catalog_id ? String(editData.alu_catalog_id) : '');
  // La galeria de fotos dejo de ser el punto de partida: el dibujo de
  // elevacion ya refleja lo capturado, mientras que la foto arrastra la
  // configuracion del proyecto del que se tomo (el 159S en el texto, el
  // OXXO fijo). Se queda como referencia visual de acabado real, colapsada.
  // Abierta de entrada solo si la linea ya venia con una foto elegida.
  const [showFinish, setShowFinish] = useState(true);
  const [showInstall, setShowInstall] = useState(true);
  const [showCat, setShowCat]   = useState(!!editData?.alu_catalog_id);
  const [catCat, setCatCat] = useState(editData?.product_type ? catalogCategory(editData.product_type) : 'Windows');
  const catalogFiltered = catalog.filter(c => catalogCategory(c.type) === catCat);
  const [calc, setCalc]         = useState(null);
  const [saving, setSaving]     = useState(false);

  // Install type auto-fill: touched=true once the vendor picks manually
  // (or when editing a line that already had one) -> stops auto-deriving.
  const [installRateTouched, setInstallRateTouched] = useState(!!editData?.buiria_rate_code);
  // Price autofill (opcion B): sigue el rec_sf mientras el vendedor no teclee
  // un precio propio. Si la linea ya venia con precio, se considera 'tocado'.
  // Se considera 'tocado' solo si el precio guardado DIFIERE del recomendado
  // que el motor calculo en su momento. Si coinciden, es que se autollenó y
  // debe volver a seguir al motor cuando cambie la especificacion.
  // Antes bastaba con que hubiera precio, y eso congelaba la linea: al cambiar
  // el vidrio (o serie, o configuracion) el precio no se actualizaba.
  // migr 099: la intencion viene marcada en la linea, no se infiere.
  const [priceTouched, setPriceTouched] = useState(!!editData?.price_manual);

  const [form, setForm] = useState({
    line_number:    editData?.line_number    || '',
    location:       editData?.location       || '',
    model_code:     editData?.model_code     || '',
    product_desc:   editData?.product_desc   || '',
    spec:           editData?.spec           || '',
    width_in:       editData?.width_in       || '',
    height_in:      editData?.height_in      || '',
    qty:            editData?.qty            || 1,
    price_sf:       editData?.price_sf       || '',
    disc_pct:       editData?.disc_pct ? parseFloat(editData.disc_pct)*100 : 0,
    include_buiria:         editData?.include_buiria         || false,
    buiria_rate_code:       editData?.buiria_rate_code       || '',
    buiria_panels:          editData?.buiria_panels          || '',
    sill_pan_lf:            editData?.sill_pan_lf            || '',
    extra_lites:            editData?.extra_lites            || 0,
    include_magnolia_labor: editData?.include_magnolia_labor || false,
    labor_scale_id:         editData?.labor_scale_id         || '',
    labor_hours:            editData?.labor_hours            || '',
    factory_cost_u: editData?.factory_cost_u || '',
    factory_quoted: editData?.factory_quoted || false,
    sort_order:     editData?.sort_order     || 0,
    alu_catalog_id: editData?.alu_catalog_id || null,
    // B.2 structured aluminum variables
    product_type:   editData?.product_type   || '',
    frame_series:   editData?.frame_series    || '',
    frame_material: editData?.frame_material  || ALU.DEFAULTS.frame_material,
    finish_system:  editData?.finish_system   || ALU.DEFAULTS.finish_system,
    finish_color:   editData?.finish_color    || ALU.DEFAULTS.finish_color,
    opens:          editData?.opens           || '',
    glazing_type:   editData?.glazing_type    || ALU.DEFAULTS.glazing_type,
    glass_makeup:   editData?.glass_makeup    || ALU.DEFAULTS.glass_makeup,
    low_e:          editData?.low_e          ?? ALU.DEFAULTS.low_e,
    argon:          editData?.argon          ?? ALU.DEFAULTS.argon,
    tempered:       editData?.tempered       ?? ALU.DEFAULTS.tempered,
    spacer:         editData?.spacer          || ALU.DEFAULTS.spacer,
    panel_config:   editData?.panel_config    || '',
    mechanism:        editData?.mechanism         || '',
    economy:          false,
    bifold_panels:    editData?.bifold_panels    || '',
    bifold_stacking:  editData?.bifold_stacking  || '',
    bifold_lock:      editData?.bifold_lock      ?? false,
    active_side:    editData?.active_side     || '',
    jamb:           editData?.jamb            || '',
    nailing_fin:    editData?.nailing_fin    ?? false,
    grilles_sdl:    editData?.grilles_sdl    ?? false,
    grilles_pattern: editData?.grilles_pattern || '',
    grille_cols:     editData?.grille_cols ?? '',
    grille_rows:     editData?.grille_rows ?? '',
    woodclad_interior_color: editData?.woodclad_interior_color || '',
    smart_glass:    editData?.smart_glass    ?? false,
    tinted_special_glass: editData?.tinted_special_glass || '',
    flange_setback: editData?.flange_setback || '',
    constraint_override: false,
    constraint_override_reason: '',
  });

  useEffect(() => {
    catalogAPI.aluModels().then(r => setModels(r.data));
    catalogAPI.aluTypeSeries().then(r => setTypeSeries(r.data));
    catalogAPI.buiriaRates().then(r => setBuiria(r.data));
    catalogAPI.ironLaborScales().then(r => setScales(r.data));
    catalogAPI.aluCatalog().then(r => setCatalog(r.data));
    catalogAPI.aluConstraints().then(r => setConstraints(r.data));
  }, []);

  useEffect(() => {
    if (form.width_in && form.height_in && scales.length) {
      const sqft = (parseFloat(form.width_in) * parseFloat(form.height_in)) / 144;
      const scale = scales.find(s =>
        (s.sqft_min == null || sqft >= s.sqft_min) &&
        (s.sqft_max == null || sqft <= s.sqft_max)
      );
      if (scale && !form.labor_scale_id) {
        setForm(f => ({ ...f, labor_scale_id: scale.id, labor_hours: f.labor_hours || scale.hours_min }));
      }
    }
  }, [form.width_in, form.height_in, scales]); // eslint-disable-line

  // Mirror installation panel count from the door's panel count.
  // A door installs with the SAME panel count it has — they cannot diverge.
  // For bi-fold the live count is bifold_panels (set the moment you pick it,
  // before stacking completes the panel_config notation); for everything else
  // it comes from panel_config (OXXO -> 4). Both are in the deps so the mirror
  // re-runs regardless of the order (install-first or panels-first).
  useEffect(() => {
    if (form.include_buiria && form.buiria_rate_code === 'DOOR_PNL') {
      const n = form.bifold_panels
        ? parseInt(form.bifold_panels)
        : ALU.panelCountFrom(form.panel_config);
      if (n != null && !Number.isNaN(n)) {
        if (String(n) !== String(form.buiria_panels)) setForm(f => ({ ...f, buiria_panels: n }));
      } else if (form.buiria_panels !== '' && form.buiria_panels != null) {
        // No se puede derivar el conteo (config sin notacion O/X): limpiar en
        // vez de dejar el valor viejo pegado -> se cobrarian paneles erroneos.
        setForm(f => ({ ...f, buiria_panels: '' }));
      }
    }
  }, [form.include_buiria, form.buiria_rate_code, form.panel_config, form.bifold_panels]); // eslint-disable-line

  // Auto-fill installation type from product_type when install is enabled.
  // Only while not manually touched -> re-derives if the vendor changes the
  // product type before choosing an install type. Chains into the panel-mirror
  // effect above (DOOR_PNL -> panels from panel_config). Mirrors kitTouched (iron).
  useEffect(() => {
    if (!form.include_buiria || installRateTouched) return;
    const def = ALU.defaultInstallRate(form.product_type, form.opens);
    if (def && def !== form.buiria_rate_code) {
      setForm(f => ({ ...f, buiria_rate_code: def }));
    }
  }, [form.include_buiria, form.product_type, form.opens, installRateTouched]); // eslint-disable-line

  const runCalc = useCallback(async () => {
    if (!form.width_in || !form.height_in || !form.model_code) { setCalc(null); return; }
    try {
      const { data } = await pricingAPI.calcAlu({
        width_in:  parseFloat(form.width_in),
        height_in: parseFloat(form.height_in),
        model_code: form.model_code,
        product_type: form.product_type || undefined,
        price_sf:   form.price_sf ? parseFloat(form.price_sf) : undefined,
        factory_cost_u: form.factory_cost_u ? parseFloat(form.factory_cost_u) : undefined,
        factory_quoted: !!form.factory_quoted,
        include_magnolia_labor: form.include_magnolia_labor,
        labor_hours: form.labor_hours ? parseFloat(form.labor_hours) : undefined,
        glass_makeup: form.glass_makeup || undefined,
        panel_config: form.panel_config || undefined,
        grilles_sdl: !!form.grilles_sdl,
        frame_material: form.frame_material || undefined,
        qty: parseInt(form.qty) || 1,
      });
      setCalc(data);
    } catch {}
  }, [form.width_in, form.height_in, form.model_code, form.product_type, form.price_sf,
      form.factory_cost_u, form.factory_quoted, form.include_magnolia_labor, form.labor_hours,
      form.glass_makeup, form.panel_config, form.grilles_sdl, form.frame_material, form.qty]);

  useEffect(() => { const t = setTimeout(runCalc, 400); return () => clearTimeout(t); }, [runCalc]);

  // Auto-fill suggested price (curve rec) when calc arrives and price is empty
  // Al abrir otra linea el componente puede no remontarse, y useState conserva
  // el valor del primer montaje. Este efecto revalua si el precio guardado fue
  // manual (difiere del recomendado) o automatico (coincide).
  useEffect(() => {
    setPriceTouched(!!editData?.price_manual);
  }, [editData?.id]); // eslint-disable-line

  useEffect(() => {
    if (calc && calc.rec_sf != null && !priceTouched) {
      setForm(f => ({ ...f, price_sf: calc.rec_sf }));
    }
  }, [calc, priceTouched]); // eslint-disable-line

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Catálogo visual: al elegir una foto de referencia, autollena
  // descripción, medidas Y la spec estructurada (product_type + opens)
  // vía CATALOG_TYPE_TO_SPEC. opens queda EDITABLE. Si el type no está
  // mapeado, no toca product_type/opens (no-op seguro = comportamiento previo).
  // Las medidas del catalogo son un EJEMPLO del proyecto de donde salio la
  // foto (migr 065: una fila por diseno, no por unidad vendida). Autollenarlas
  // hacia que el vendedor arrastrara medidas ajenas sin notarlo. La foto llena
  // descripcion y spec; las medidas las teclea el.
  const pickCatalog = (val, opt) => {
    setCatSel(val);
    if (!opt) return;
    const spec = ALU.specForCatalogType(opt.type);
    setForm(f => ({
      ...f,
      alu_catalog_id: opt.id,
      product_desc: (opt.spec_raw || '').replace(/^\(\d+S\)\s*/i, '').trim() || f.product_desc,
      ...(spec ? { product_type: spec.product_type, opens: spec.opens, ...(spec.mechanism ? { mechanism: spec.mechanism } : {}) } : {}),
    }));
  };

  const netSales = () => {
    if (!calc || !form.price_sf) return null;
    return parseFloat(form.price_sf) * calc.sqft_unit * parseInt(form.qty||1) * (1 - parseFloat(form.disc_pct||0)/100);
  };

  // Aviso pendiente A: linea sin precio sincroniza al SO en $0
  const noPrice = !form.price_sf || parseFloat(form.price_sf) === 0;

  const handleSave = async () => {
    if (!form.model_code || !form.width_in || !form.height_in)
      return toast.error('Model, width and height required');
    if (!form.product_type)
      return toast.error('Product Type is required (use "Custom" for one-offs)');
    if (ALU.opensFor(form.product_type).length && !form.opens)
      return toast.error('Opens is required for ' + form.product_type);
    if (saveBlocked)
      return toast.error('Outside factory limits: ' + describeViolation(cons.violations[0]));
    if (!isManager && parseFloat(form.disc_pct || 0) > 5)
      return toast.error('Discount above 5% requires manager approval');
    if (noPrice) {
      toast('No price set — this line will sync to the order at $0', { icon: '⚠️' });
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        track_count: resolved.model ? (resolved.model.track_count ?? null) : null,
        product_desc:   form.product_desc || ALU.productDescFor(form.product_type),
        width_in:       parseFloat(form.width_in),
        height_in:      parseFloat(form.height_in),
        qty:            parseInt(form.qty),
        price_sf:       form.price_sf ? parseFloat(form.price_sf) : undefined,
        disc_pct:       parseFloat(form.disc_pct||0) / 100,
        argon:          ALU.argonFromMakeup(form.glass_makeup),
        constraint_override: !!(isManager && cons.blocked && form.constraint_override),
        constraint_override_reason: form.constraint_override_reason || undefined,
        price_manual: priceTouched,
        factory_cost_u: form.factory_cost_u ? parseFloat(form.factory_cost_u) : undefined,
        factory_quoted: !!form.factory_quoted,
        buiria_panels:  form.buiria_panels ? parseInt(form.buiria_panels) : undefined,
        sill_pan_lf:    form.sill_pan_lf   ? parseFloat(form.sill_pan_lf) : undefined,
        extra_lites:    parseInt(form.extra_lites||0),
        labor_scale_id: form.labor_scale_id || undefined,
        labor_hours:    form.labor_hours ? parseFloat(form.labor_hours) : undefined,
        flange_setback: form.flange_setback !== '' && form.flange_setback != null
                          ? parseFloat(form.flange_setback) : undefined,
        grille_cols:    form.grille_cols !== '' && form.grille_cols != null
                          ? parseInt(form.grille_cols) : null,
        grille_rows:    form.grille_rows !== '' && form.grille_rows != null
                          ? parseInt(form.grille_rows) : null,
      };
      // Paso 3 familia (21-oct-2026): pieza de la otra familia -> se manda la confirmacion; si el
      // backend responde 409 CROSS_FAMILY se pide confirmar y se reintenta una vez.
      if (editData?.id) {
        await sendWithCrossFamilyAck(p => quotesAPI.updateAluLine(quoteId, editData.id, p), payload);
        toast.success('Line updated');
      } else {
        await sendWithCrossFamilyAck(p => quotesAPI.addAluLine(quoteId, p),
          crossFamilyAck ? { ...payload, cross_family_ack: true } : payload);
        toast.success('Line added');
      }
      onSaved();
    } catch (err) {
      if (err.cancelled) return;
      toast.error(err.response?.data?.error || 'Save failed');
    }
    finally       { setSaving(false); }
  };

  const alertMeta  = calc?.alert ? ALERT_META[calc.alert] : null;
  const isBuiria   = installer === 'buiria';
  const isMagnolia = installer === 'magnolia';
  // Series resolver: derive the exact model from product_type + mechanism +
  // panel_config (migr 056 data via enriched alu-type-series endpoint). The
  // vendor thinks in products; the series is an engineering outcome.
  const candidates = typeSeries[form.product_type];
  const resolved = resolveSeries(candidates, {
    mechanism: form.mechanism, panel_config: form.panel_config,
    economy: !!form.economy,
  });
  const isCustomType = form.product_type === 'Custom';
  const customModels = isCustomType ? models : [];

  // Limites de fabrica (migr 061/062). Funcion pura sobre datos que ya estan
  // en form -> se evalua en cada render, sin estado propio.
  // D.14: el conteo de paneles sale de la MISMA expresion que el espejo de
  // instalacion. Una sola fuente o los dos numeros divergen.
  const panelCount = form.bifold_panels
    ? parseInt(form.bifold_panels)
    : ALU.panelCountFrom(form.panel_config);
  const cons = validateAluLine({
    series:         resolved.model ? resolved.model.code : form.model_code,
    product_type:   form.product_type,
    frame_material: form.frame_material,
    opens:          form.opens,
    width_in:       form.width_in,
    height_in:      form.height_in,
    panel_count:    panelCount,
  }, constraints);

  // Override (D.4): solo manager/admin, y solo con razon escrita. El CHECK de
  // la migracion 061 lo exige tambien en base, asi que un POST directo sin
  // razon lo rechaza Postgres. Aqui solo se evita llegar hasta alla.
  const canOverride  = isManager && cons.blocked;
  const overrideOk   = !!form.constraint_override
    && String(form.constraint_override_reason || '').trim().length > 0;
  const saveBlocked  = cons.blocked && !(isManager && overrideOk);

  const modelHint =
    !form.product_type
      ? 'Select a product type first'
      : (!candidates && !isCustomType)
      ? 'Factory series pending for this type'
      : resolved.needsMechanism && !form.mechanism
      ? 'Choose a mechanism to resolve the model'
      : '';

  useEffect(() => {
    if (isCustomType) return;
    const code = resolved.model ? resolved.model.code : '';
    if (code !== form.model_code) set('model_code', code);
  }, [resolved.model && resolved.model.code, isCustomType]); // eslint-disable-line

  // Reopen safety: economy is not persisted. If the line was saved with the
  // cheapest model of its pool (152S), the vendor had asked for economy, so we
  // rebuild economy=true to stop the model_code effect from reverting to 159S.
  useEffect(() => {
    if (isCustomType || !editData?.model_code) return;
    const pool = typeSeries[form.product_type];
    if (!Array.isArray(pool) || pool.length < 2) return;
    const withF = pool.filter(c => c.factor != null);
    if (withF.length < 2) return;
    const cheapest = withF.reduce((a, b) => Number(b.factor) < Number(a.factor) ? b : a);
    if (cheapest.code === editData.model_code && !form.economy) set('economy', true);
  }, [typeSeries, form.product_type]); // eslint-disable-line

  // Al cambiar el glazing, forzar un makeup valido al cambiar de tipo:
  // el filtro cambia las OPCIONES pero no corrige el valor ya elegido
  // (ej. Triple + 6+12Ar+6 quedaria como combinacion imposible).
  useEffect(() => {
    const valid = ALU.makeupsFor(form.glazing_type);
    if (form.glass_makeup && !valid.includes(form.glass_makeup)) {
      set('glass_makeup', valid[0] || '');
    }
  }, [form.glazing_type]); // eslint-disable-line

  // 152S topa en 2 rieles (nota tecnica Doorwin, SO11197). Con 3 o 4 rieles
  // la opcion economy no existe: se oculta el checkbox y se apaga el valor
  // si ya venia marcado, para que no quede una config que fabrica no hace.
  const rielesActuales = tracksFromConfig(form.panel_config);
  const economyDisponible = !rielesActuales || rielesActuales.tracks <= 2;
  useEffect(() => {
    if (!economyDisponible && form.economy) set('economy', false);
  }, [economyDisponible]); // eslint-disable-line
  // Sliding Door: Opens espeja el mecanismo (mismo dato, una sola fuente).
  useEffect(() => {
    if (form.product_type !== 'Sliding Door') return;
    if (form.mechanism && form.opens !== form.mechanism) {
      set('opens', form.mechanism);
    }
  }, [form.product_type, form.mechanism]); // eslint-disable-line

  return (
    <div className="modal-overlay">
      <div className="modal modal-alu modal-flex">
        <div className="modal-header">
          <span className="modal-title">{editData?.id ? 'Edit' : 'Add'} Aluminum Line</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">

          {/* Installer badge */}
          <div className="flex items-center gap-2" style={{ marginBottom: 16 }}>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Installer:</span>
            <span className={`badge ${isBuiria ? 'badge-blue' : isMagnolia ? 'badge-green' : 'badge-gray'}`}>
              {isBuiria || isMagnolia ? 'With installation' : 'No installation'}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
              — set at quote level
            </span>
          </div>

          <div style={{ marginBottom: 12 }}>
            <button type="button" onClick={() => setShowCat(v => !v)}
              style={{ background: 'transparent', border: 'none', padding: 0,
                       cursor: 'pointer', fontSize: 12, color: 'var(--text-2)',
                       display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10 }}>{showCat ? '\u25BC' : '\u25B6'}</span>
              {showCat ? 'Hide reference photos' : 'Reference photos (optional)'}
              {catSel && !showCat && (
                <span style={{ color: 'var(--gold, #b8995f)', fontWeight: 600 }}>
                  &middot; 1 selected
                </span>
              )}
            </button>
          </div>
          <div className="form-row" style={{ display: showCat ? undefined : 'none' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">
                Reference catalog
                <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 6 }}>
                  — auto-fills description and specifications
                </span>
              </label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                {CATALOG_CATEGORIES.map(cat => (
                  <button key={cat} type="button" onClick={() => setCatCat(cat)}
                    style={{ flex: 1, padding: '6px 0', fontSize: 13, cursor: 'pointer',
                      background: catCat === cat ? 'var(--bg-3)' : 'transparent',
                      fontWeight: catCat === cat ? 600 : 400,
                      border: '1px solid var(--border)', borderRadius: 6 }}>
                    {cat}
                  </button>
                ))}
              </div>
              <SearchSelect
                options={catalogFiltered}
                value={catSel}
                onChange={pickCatalog}
                getValue={c => String(c.id)}
                getLabel={c => (c.spec_raw || '').replace(/^\(\d+S\)\s*/i, '').trim() || `${c.type} - ${c.line}`}
                getSub={c => `${catalogBlurb(c.type)}  |  e.g. ${c.width_in || '?'}x${c.height_in || '?'}"`}
                getImage={c => c.image_url}
                thumbClass="alu-cat-thumb"
                zoomOnHover={true}
                placeholder="Search catalog product…"
                allowEmpty={true}
              />
            </div>
          </div>
          <div className="alu-modal-grid">
            <div className="alu-modal-form">
          <div className="form-row-3">
            <div className="form-group">
              <label className="form-label">Width (in) *</label>
              <input className="form-input text-mono" type="number" step="0.25"
                value={form.width_in} onChange={e => set('width_in', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Height (in) *</label>
              <input className="form-input text-mono" type="number" step="0.25"
                value={form.height_in} onChange={e => set('height_in', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Qty</label>
              <input className="form-input text-mono" type="number" min="1"
                value={form.qty} onChange={e => set('qty', e.target.value)} />
            </div>
          </div>

          {cons.limits.length > 0 && cons.violations.length === 0 && (
            <div style={{ background: 'var(--bg-3)', border: '1px solid var(--border)',
                          borderRadius: 8, padding: '8px 12px', marginBottom: 16,
                          fontSize: 12, color: 'var(--text-2)' }}>
              <span style={{ fontWeight: 600 }}>Factory limits: </span>
              {cons.limits
                .filter(l => l.max_w_in || l.max_h_in || l.max_area_sqft
                          || l.min_w_in || l.min_h_in)
                .map((l, i) => {
                  // Label W and H explicitly: "84x120" alone doesn't tell the
                  // vendor which number is which. Also skip nulls instead of
                  // rendering Number(null) as 0.
                  const bits = [];
                  if (l.min_w_in && l.max_w_in) bits.push('W ' + Number(l.min_w_in) + '-' + Number(l.max_w_in) + '"');
                  else if (l.max_w_in)          bits.push('W max ' + Number(l.max_w_in) + '"');
                  else if (l.min_w_in)          bits.push('W min ' + Number(l.min_w_in) + '"');
                  if (l.min_h_in && l.max_h_in) bits.push('H ' + Number(l.min_h_in) + '-' + Number(l.max_h_in) + '"');
                  else if (l.max_h_in)          bits.push('H max ' + Number(l.max_h_in) + '"');
                  else if (l.min_h_in)          bits.push('H min ' + Number(l.min_h_in) + '"');
                  if (l.max_area_sqft)          bits.push('area max ' + Number(l.max_area_sqft) + ' sf');
                  const ambito = l.scope === 'panel' ? 'per panel'
                               : l.scope === 'system' ? 'whole system'
                               : 'per unit';
                  return (
                    <span key={i} style={{ marginRight: 18, whiteSpace: 'nowrap' }}>
                      <span style={{ opacity: 0.75 }}>{ambito}:</span>{' '}
                      {bits.join(' \u00B7 ')}
                      {l.modular ? <span style={{ opacity: 0.75 }}> (modular)</span> : null}
                      {l.confidence === 'low'
                        ? <span title="Pending factory confirmation"
                                style={{ opacity: 0.75 }}> *</span>
                        : null}
                    </span>
                  );
                })}
              {cons.limits.some(l => l.confidence === 'low') && (
                <div style={{ marginTop: 4, opacity: 0.75 }}>
                  * pending factory confirmation
                </div>
              )}
            </div>
          )}

          {cons.violations.length > 0 && (
            <div style={{ borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                          fontSize: 12, fontWeight: 600,
                          background: cons.blocked ? 'var(--orange-bg)' : 'var(--bg-3)',
                          border: '1px solid ' + (cons.blocked ? 'var(--orange)' : 'var(--border)'),
                          color: cons.blocked ? 'var(--orange)' : 'var(--text-2)' }}>
              <div style={{ marginBottom: 4 }}>
                {cons.blocked
                  ? 'Outside factory limits:'
                  : 'Exceeds standard module - additional modules required:'}
              </div>
              {cons.violations.map((v, i) => (
                <div key={i} style={{ fontWeight: 400 }}>- {describeViolation(v)}</div>
              ))}
            </div>
          )}

          {canOverride && (
            <div style={{ border: '1px solid var(--orange)', borderRadius: 8,
                          padding: '10px 14px', marginBottom: 16, background: 'var(--bg-2)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8,
                              cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                <input type="checkbox" checked={!!form.constraint_override}
                  onChange={e => set('constraint_override', e.target.checked)} />
                <span>Override factory limit (manager approval)</span>
              </label>
              {form.constraint_override ? (
                <div className="form-group" style={{ marginTop: 10 }}>
                  <label className="form-label">Reason *</label>
                  <input className="form-input"
                    placeholder="e.g. factory confirmed exception by email"
                    value={form.constraint_override_reason}
                    onChange={e => set('constraint_override_reason', e.target.value)} />
                  <div className="form-hint">
                    Recorded with your name and the date. Not shown on the client PDF.
                  </div>
                </div>
              ) : (
                <div className="form-hint" style={{ marginTop: 6 }}>
                  Only check this if the factory confirmed the exception.
                </div>
              )}
            </div>
          )}

          {/* SPECIFICATIONS (B.2) */}
          <div style={{ background:'var(--bg-3)', border:'1px solid var(--border)', borderRadius:8, padding:14, marginBottom:16 }}>
            <div style={{ fontWeight:600, fontSize:13, marginBottom:12 }}>Specifications</div>

            <div className="form-row">
              <CatalogField label="Product Type *" options={ALU.PRODUCT_TYPES}
                value={form.product_type}
                onChange={v => setForm(f => ({ ...f, product_type: v,
                  opens: ALU.defaultOpensFor(v, f.mechanism),
                  product_desc: ALU.productDescFor(v) }))} />
            </div>


            {(resolved.needsMechanism || ALU.showPanelConfig(form.product_type, form.opens) || form.opens === 'Bi-fold / Folding') && (
              <div className="form-row">
                {resolved.needsMechanism ? (
                <div className="form-group">
                  <label className="form-label">Mechanism *</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {resolved.mechanismOptions.map(opt => (
                      <button type="button" key={opt}
                        onClick={() => set('mechanism', opt)}
                        style={{ flex: 1, padding: '8px 0', fontSize: 13, cursor: 'pointer',
                          borderRadius: 6,
                          border: form.mechanism === opt ? '2px solid #b8995f' : '1px solid var(--border)',
                          background: form.mechanism === opt ? 'rgba(184,153,95,0.10)' : '#fff',
                          fontWeight: form.mechanism === opt ? 600 : 400 }}>
                        {opt}
                      </button>
                    ))}
                  </div>
                  <div className="form-hint">The exact series is derived from mechanism + panels.</div>
                </div>
                ) : <div className="form-group" />}
              {form.opens === 'Bi-fold / Folding' ? (
                <div style={{ flex: 1 }}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label"># Panels</label>
                      <select className="form-input" value={form.bifold_panels}
                        onChange={e => {
                          const n = e.target.value;
                          const allowed = ALU.bifoldStackingOptions(n).map(o => o.key);
                          const keepStack = allowed.includes(form.bifold_stacking) ? form.bifold_stacking : '';
                          const keepLock = ALU.bifoldAllowsExteriorKey(n) ? form.bifold_lock : false;
                          setForm(f => ({ ...f, bifold_panels: n, bifold_stacking: keepStack, bifold_lock: keepLock,
                            panel_config: ALU.bifoldNotation(n, keepStack, keepLock) }));
                        }}>
                        <option value="">Select…</option>
                        {ALU.BIFOLD_PANELS.map(n => <option key={n} value={n}>{n} panels</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Stacking</label>
                      <select className="form-input" value={form.bifold_stacking}
                        disabled={!form.bifold_panels}
                        onChange={e => {
                          const st = e.target.value;
                          setForm(f => ({ ...f, bifold_stacking: st,
                            panel_config: ALU.bifoldNotation(f.bifold_panels, st, f.bifold_lock) }));
                        }}>
                        <option value="">Select…</option>
                        {ALU.bifoldStackingOptions(form.bifold_panels).map(o =>
                          <option key={o.key} value={o.key}>{o.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-row" style={{ alignItems: 'center', gap: 16 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Swing</label>
                      <input className="form-input" value="Outswing" readOnly disabled />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                      color: ALU.bifoldAllowsExteriorKey(form.bifold_panels) ? 'inherit' : 'var(--text-3)' }}>
                      <input type="checkbox" checked={form.bifold_lock}
                        disabled={!ALU.bifoldAllowsExteriorKey(form.bifold_panels)}
                        onChange={e => {
                          const lk = e.target.checked;
                          setForm(f => ({ ...f, bifold_lock: lk,
                            panel_config: ALU.bifoldNotation(f.bifold_panels, f.bifold_stacking, lk) }));
                        }} />
                      Exterior key
                    </label>
                  </div>
                  {form.bifold_panels && !ALU.bifoldAllowsExteriorKey(form.bifold_panels) && (
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
                      Even panel count: no exterior key (lock every 2 panels).
                    </div>
                  )}
                  {form.panel_config && (
                    <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>
                      Config: <span className="text-mono">{form.panel_config}</span>
                    </div>
                  )}
                </div>
              ) : ALU.showPanelConfig(form.product_type, form.opens) ? (
                <CatalogField label="Panel Configuration" options={ALU.PANEL_CONFIGS}
                  value={form.panel_config}
                  onChange={v => set('panel_config', /^[oxOX]+$/.test(v) ? v.toUpperCase() : v)}
                  hint="O = fixed panel · X = operable. Read left→right from outside (e.g. XO = sliding left, fixed right). LFO/RFO = hinge side (Left/Right From Outside)." />
              ) : <div className="form-group" />}
              </div>
            )}
            <div className="form-row">
              <CatalogField label="Opens *" options={ALU.opensFor(form.product_type, form.mechanism)}
                value={form.opens} onChange={v => set('opens', v)}
                auto={!!form.opens
                  && form.opens === ALU.defaultOpensFor(form.product_type, form.mechanism)} />
              <div className="form-group" />
            </div>
            {form.mechanism === 'Lift-Slide' && economyDisponible && (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-checkbox" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox"
                      checked={!!form.economy}
                      onChange={e => set('economy', e.target.checked)} />
                    <span>Economy profile (152S) - regular frame, max 2 tracks</span>
                  </label>
                  <div className="form-hint">
                    Default is 159S: narrow frame, higher price, and the only one that does multi-track. Check only if the client asked for the cheaper profile.
                  </div>
                </div>
                <div className="form-group" />
              </div>
            )}
            <div onClick={() => setShowFinish(v => !v)}
              style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer',
                fontWeight:600, fontSize:12, color:'var(--text-3)', margin:'8px 0' }}>
              <span>{showFinish ? '\u25be' : '\u25b8'}</span>
              <span>Materials, finish and glass</span>
              {!showFinish && (
                <span style={{ fontWeight:400, color:'var(--text-3)' }}>
                  {[form.frame_material, form.finish_color, form.glass_makeup]
                    .filter(Boolean).join(' \u00b7 ')}
                </span>
              )}
            </div>
            {showFinish && (<>
            <div className="form-row">
              <CatalogField label="Frame Material" options={ALU.FRAME_MATERIALS}
                value={form.frame_material} onChange={v => set('frame_material', v)} allowCustom={false} />
              <div className="form-group" />
            </div>

            <div className="form-row">
              <CatalogField label="Finish System" options={ALU.FINISH_SYSTEMS}
                value={form.finish_system} onChange={v => set('finish_system', v)} />
              <CatalogField label="Finish Color" options={ALU.FINISH_COLORS}
                value={form.finish_color} onChange={v => set('finish_color', v)} allowCustom={false} />
            </div>

            <div style={{ fontWeight:600, fontSize:12, color:'var(--text-3)', margin:'8px 0' }}>Glass</div>
            <div className="form-row">
              <CatalogField label="Glazing Type" options={ALU.GLAZING_TYPES}
                value={form.glazing_type} onChange={v => set('glazing_type', v)} allowCustom={false} />
              <CatalogField label="Make-up" options={ALU.makeupsFor(form.glazing_type)}
                value={form.glass_makeup} onChange={v => set('glass_makeup', v)} />
            </div>
            <div className="form-row-3">
              <label className="flex items-center gap-2" style={{ fontSize:13, cursor:'pointer' }}>
                <input type="checkbox" checked={form.low_e} onChange={e => set('low_e', e.target.checked)} /> Low-E
              </label>
              <label className="flex items-center gap-2" style={{ fontSize:13, cursor:'pointer' }}>
                <input type="checkbox" checked={form.tempered} onChange={e => set('tempered', e.target.checked)} /> Tempered
              </label>
            </div>
            <div className="form-row">
              <CatalogField label="Spacer" options={ALU.SPACERS}
                value={form.spacer} onChange={v => set('spacer', v)} allowCustom={false} />
            </div>
            </>)}
            {ALU.showActiveSide(form.product_type, form.opens) && (
              <div className="form-row">
                <CatalogField label="Active Side" options={ALU.activeSidesFor(form.opens)}
                  value={form.active_side} onChange={v => set('active_side', v)} allowCustom={false} />
              </div>
            )}
            <div onClick={() => setShowInstall(v => !v)}
              style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer',
                fontWeight:600, fontSize:12, color:'var(--text-3)', margin:'8px 0' }}>
              <span>{showInstall ? '\u25be' : '\u25b8'}</span>
              <span>Installation details and options</span>
              {!showInstall && (
                <span style={{ fontWeight:400, color:'var(--text-3)' }}>
                  {[form.jamb && ('Jamb ' + form.jamb), form.nailing_fin && 'Nailing fin',
                    form.grilles_sdl && 'Grilles', form.smart_glass && 'Smart glass']
                    .filter(Boolean).join(' \u00b7 ') || 'factory standard'}
                </span>
              )}
            </div>
            {showInstall && (<>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Jamb</label>
                <input className="form-input" placeholder='e.g. 3 1/2"' value={form.jamb}
                  onChange={e => set('jamb', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">&nbsp;</label>
                <label className="flex items-center gap-2" style={{ fontSize:13, cursor:'pointer', paddingTop:8 }}>
                  <input type="checkbox" checked={form.nailing_fin}
                    onChange={e => set('nailing_fin', e.target.checked)} /> Nailing Fin
                </label>
              </div>
            </div>

            {ALU.showWoodCladInterior(form.frame_material) && (
              <div className="form-row">
                <div className="form-group">
                  <CatalogField label="Wood-Clad Interior Paint" options={ALU.FINISH_COLORS}
                    value={form.woodclad_interior_color} onChange={v => set('woodclad_interior_color', v)} />
                </div>
              </div>
            )}
            <div className="form-row-3">
              <label className="flex items-center gap-2" style={{ fontSize:13, cursor:'pointer' }}>
                <input type="checkbox" checked={form.grilles_sdl} onChange={e => {
                  const on = e.target.checked; set('grilles_sdl', on);
                  if (on && !form.grilles_pattern) { set('grilles_pattern', 'Colonial'); if (form.grille_cols === '') set('grille_cols', 3); if (form.grille_rows === '') set('grille_rows', 3); }
                }} /> Grilles / SDL
              </label>
              <label className="flex items-center gap-2" style={{ fontSize:13, cursor:'pointer' }}>
                <input type="checkbox" checked={form.smart_glass} onChange={e => set('smart_glass', e.target.checked)} /> Smart Glass
              </label>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">Flange Setback (in)</label>
                <input className="form-input text-mono" type="number" step="0.25" placeholder="optional"
                  value={form.flange_setback} onChange={e => set('flange_setback', e.target.value)} />
                <div className="form-hint" style={{ marginTop:4 }}>
                  How far the nail fin sits back from the front of the frame, to match exterior wall thickness.
                  Typical: 1" stucco &middot; 1.5" brick veneer. Leave blank for factory standard.
                </div>
              </div>
            </div>
            </>)}
            {form.grilles_sdl && (
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">Grille Pattern</label>
                <GrillePatternSelect value={form.grilles_pattern} onType={v => set('grilles_pattern', v)} />
                {(/^colonial$/i.test(form.grilles_pattern) || /^custom/i.test(form.grilles_pattern)) && (
                  <>
                    <div className="form-row-3" style={{ marginTop:8 }}>
                      <div className="form-group" style={{ marginBottom:0 }}>
                        <label className="form-label">Panes Wide</label>
                        <input className="form-input text-mono" type="number" min="1" step="1"
                          placeholder="e.g. 3" value={form.grille_cols}
                          onChange={e => set('grille_cols', e.target.value)} />
                      </div>
                      <div className="form-group" style={{ marginBottom:0 }}>
                        <label className="form-label">Panes Tall</label>
                        <input className="form-input text-mono" type="number" min="1" step="1"
                          placeholder="e.g. 3" value={form.grille_rows}
                          onChange={e => set('grille_rows', e.target.value)} />
                      </div>
                      {/^custom/i.test(form.grilles_pattern) ? (
                        <div className="form-group" style={{ marginBottom:0 }}>
                          <label className="form-label">Note</label>
                          <input className="form-input" placeholder="e.g. cottage"
                            value={form.grilles_pattern.replace(/^custom\s*/i, '')}
                            onChange={e => set('grilles_pattern', 'Custom' + (e.target.value ? ' ' + e.target.value : ''))} />
                        </div>
                      ) : <div />}
                    </div>
                    <div className="form-hint" style={{ marginTop:4 }}>e.g. 3 wide × 3 tall = 9 panes</div>
                  </>
                )}
              </div>
            )}
          </div>

          <div style={{ fontWeight:600, fontSize:12, color:'var(--text-3)', margin:'8px 0' }}>Resolved model</div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Model *</label>
              {modelHint && <div className="field-hint" style={{ color: '#b8995f', fontSize: '0.8rem', marginBottom: '4px' }}>{modelHint}</div>}
              {isCustomType ? (
                <SearchSelect
                  options={customModels}
                  value={form.model_code}
                  onChange={v => set('model_code', v)}
                  getValue={m => m.code}
                  getLabel={m => m.code}
                  getSub={m => isManager
                    ? (m.description ? m.description + ' (' + m.factor + ')' : 'Factor ' + m.factor)
                    : (m.description || '')}
                  placeholder="Type to search..."
                  allowEmpty={false}
                />
              ) : resolved.model ? (
                <div style={{ border: '1px solid #b8995f', borderRadius: 8, padding: '10px 12px',
                              background: 'rgba(184,153,95,0.06)' }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)' }}>
                    {resolved.model.display_name || resolved.model.code}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                    <span className="text-mono">{resolved.model.code}</span>
                    {resolved.model.thickness_class ? ' - ' + resolved.model.thickness_class : ''}
                  </div>
                  {resolved.rough && (
                    <div style={{ fontSize: 11, color: '#b45309', marginTop: 6, fontWeight: 600 }}>
                      Preliminary estimate - factory confirms the exact series.
                    </div>
                  )}
                </div>
              ) : (
                <div className="form-input" style={{ background: 'var(--bg-3)', cursor: 'default',
                     color: 'var(--text-3)', display: 'flex', alignItems: 'center' }}>
                  {resolved.needsMechanism && !form.mechanism
                    ? 'Choose a mechanism above'
                    : 'Select a product type'}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Product Description</label>
              <input className="form-input" placeholder="Fixed Window, Tilt-Turn…" value={form.product_desc}
                onChange={e => set('product_desc', e.target.value)} />
            </div>
          </div>
          <div style={{ fontWeight:600, fontSize:12, color:'var(--text-3)', margin:'8px 0' }}>Line details</div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Line #</label>
              <input className="form-input" placeholder="W01" value={form.line_number}
                onChange={e => set('line_number', e.target.value)} />
            </div>
            <CatalogField label="Location" options={ALU.LOCATIONS}
              value={form.location} onChange={v => set('location', v)} />
          </div>
          {/* Live pricing */}
          {calc && (
            <div style={{ background:'var(--bg-3)', border:'1px solid var(--border)', borderRadius:8, padding:14, marginBottom:16, fontSize:12 }}>

              {/* Origen del precio (migr 094/095). El catalogo trae el precio
                  real que Doorwin ya cotizo para esta combinacion. Los numeros
                  de orden solo los ve manager/admin. */}
              {(calc.precio_origen === 'catalogo' || calc.precio_origen === 'tarifa') ? (
                <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid var(--border)',
                              fontSize:11, color:'var(--text-3)' }}>
                  <span style={{ fontWeight:600, color: calc.precio_origen === 'catalogo' ? '#2F6B4F' : '#5B6770' }}>{calc.precio_origen === 'catalogo' ? 'Catalog price' : 'Standard rate'}</span>
                  {' — '}{calc.catalogo_n_lineas} quote{calc.catalogo_n_lineas === 1 ? '' : 's'}
                  {calc.precio_origen === 'catalogo' ? (' across ' + calc.catalogo_n_proy + ' project' + (calc.catalogo_n_proy === 1 ? '' : 's')) : (' in ' + calc.catalogo_n_proy + ' size' + (calc.catalogo_n_proy === 1 ? '' : 's'))}
                  {calc.catalogo_confianza ? ' - ' + ({ estable:'stable', normal:'normal', variable:'variable', dispersa:'scattered', unica:'single' }[calc.catalogo_confianza] || calc.catalogo_confianza) : ''}
                  {isManager && calc.catalogo_fob_sf
                    ? ' · FOB $' + calc.catalogo_fob_sf + '/sf' : ''}
                  {calc.glass_adj_nota && (
                    <div style={{ marginTop:3, color:'#b8995f' }}>
                      glass adj: {calc.glass_adj_nota}
                    </div>
                  )}
                  {isManager && calc.catalogo_sos && (
                    <div style={{ marginTop:4, fontFamily:'monospace', fontSize:10 }}>
                      {calc.catalogo_sos}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid var(--border)',
                              fontSize:11, color:'var(--text-3)' }}>
                  <span style={{ fontWeight:600, color:'#b8995f' }}>Preliminary estimate</span>
                  {' — no precedent in quote history. Factory confirms the price.'}
                </div>
              )}
            </div>
          )}


          {/* Cotizacion de fabrica (solo admin/manager).
              Dos cosas SEPARADAS a proposito: anotar el costo que dio la planta
              se puede hacer siempre, y la casilla es lo que lo vuelve definitivo
              y deriva el precio. Antes el campo estaba escondido detras de la
              casilla, asi que para anotar un costo de Doorwin habia que
              declararlo firme -- inservible para ir calibrando. */}
          {isManager && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8,
                          padding: '10px 14px', marginBottom: 16, background: 'var(--bg-2)' }}>
              <div className="form-group" style={{ maxWidth: 280, marginBottom: 12 }}>
                <label className="form-label">Factory cost / unit (FOB)</label>
                <input className="form-input text-mono" type="number" step="0.01"
                  placeholder="0.00"
                  value={form.factory_cost_u}
                  onChange={e => set('factory_cost_u', e.target.value)} />
                <div className="form-hint">
                  What the plant quoted. Safe to record while still comparing.
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8,
                              cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                <input type="checkbox"
                  checked={!!form.factory_quoted}
                  onChange={e => set('factory_quoted', e.target.checked)} />
                <span>Factory cost is final (derives price and margin)</span>
              </label>
              <div className="form-hint" style={{ marginTop: 6 }}>
                {form.factory_quoted
                  ? (priceTouched
                      ? 'Price was set by hand, so it is kept. Margin is computed against the factory cost.'
                      : 'On save, price and margin are recalculated from this cost.')
                  : 'Cost and margin stay a preliminary estimate until you check this.'}
              </div>
            </div>
          )}

          {/* Aviso para Vendedor: precio preliminar (sin costos ni margen) */}
          {!isManager && !form.factory_quoted && (
            <div style={{ background: 'var(--bg-3)', border: '1px solid var(--border)',
                          borderRadius: 8, padding: '8px 12px', marginBottom: 16,
                          fontSize: 12, color: 'var(--text-2)' }}>
              Preliminary price - subject to the plant quote.
            </div>
          )}

          {/* Aviso: linea sin precio (pendiente A) */}
          {noPrice && form.model_code && form.width_in && form.height_in && (
            <div style={{ background:'var(--orange-bg)', border:'1px solid var(--orange)',
                          borderRadius:8, padding:'10px 14px', marginBottom:16,
                          fontSize:12, color:'var(--orange)', fontWeight:600 }}>
              ⚠️ No price set — this line will sync to the order at <span className="text-mono">$0.00</span>.
              Enter Price $/sf (suggested: {calc ? fmt.currency(calc.rec_sf) : '—'}/sf).
            </div>
          )}

          {/* ── BUIRIA INSTALLATION ── */}
          {isBuiria && (
            <div style={{ background:'var(--bg-3)', borderRadius:8, padding:14, marginBottom:16, border:'1px solid var(--border)' }}>
              <label className="flex items-center gap-2" style={{ cursor:'pointer', marginBottom: form.include_buiria ? 12 : 0 }}>
                <input type="checkbox" checked={form.include_buiria}
                  onChange={e => set('include_buiria', e.target.checked)} />
                <span style={{ fontWeight:600, fontSize:13 }}>Include Installation</span>
              </label>
              {form.include_buiria && (
                <div>
                  <div className="form-group" style={{ marginBottom:10 }}>
                    <label className="form-label">Installation Type</label>
                    <SearchSelect
                      options={buiria}
                      value={form.buiria_rate_code}
                      onChange={v => { set('buiria_rate_code', v); setInstallRateTouched(true); }}
                      getValue={r => r.code}
                      getLabel={r => r.description}
                      getSub={r => (r.unit === '$/unit' ? `$${r.rate}/unit` : `$${r.rate}/${String(r.unit).replace('$/','')}`)}
                      placeholder="Type to search…"
                      allowEmpty={false}
                    />
                  </div>
                  {form.buiria_rate_code === 'DOOR_PNL' && (
                    <div className="form-row">
                      <div className="form-group" style={{ marginBottom:0 }}>
                        <label className="form-label"># Panels</label>
                        <input className="form-input text-mono" type="number" min="1"
                          value={form.buiria_panels} onChange={e => set('buiria_panels', e.target.value)} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── MAGNOLIA OWN LABOR ── */}
          {isMagnolia && (
            <div style={{ background:'var(--green-bg)', borderRadius:8, padding:14, marginBottom:16, border:'1px solid rgba(46,143,93,0.3)' }}>
              <label className="flex items-center gap-2" style={{ cursor:'pointer', marginBottom: form.include_magnolia_labor ? 4 : 0 }}>
                <input type="checkbox" checked={form.include_magnolia_labor}
                  onChange={e => set('include_magnolia_labor', e.target.checked)} />
                <span style={{ fontWeight:600, fontSize:13, color:'var(--green)' }}>🟢 Include Magnolia Installation Labor</span>
              </label>
              <div style={{ fontSize:11, color:'var(--text-3)', marginBottom: form.include_magnolia_labor ? 12 : 0 }}>
                3 persons × $57/hr · Not subject to sales tax
              </div>
              {form.include_magnolia_labor && (
                <div className="form-row">
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Labor Scale</label>
                    <SearchSelect
                      options={scales}
                      value={form.labor_scale_id}
                      onChange={(v, s) => {
                        set('labor_scale_id', v);
                        if (s) set('labor_hours', s.hours_min);
                      }}
                      getLabel={s => s.label}
                      getSub={s => `${s.hours_min}${s.hours_max ? `–${s.hours_max}` : '+'} hrs`}
                      placeholder="Type to search…"
                      allowEmpty={false}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Hours</label>
                    <input className="form-input text-mono" type="number" step="0.5" min="1"
                      placeholder="auto" value={form.labor_hours}
                      onChange={e => set('labor_hours', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Labor Total</label>
                    <div className="form-input text-mono" style={{ background:'var(--bg-3)', cursor:'default', color:'var(--green)' }}>
                      {calc?.labor_total != null ? fmt.currency(calc.labor_total) : '—'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {!form.product_type ? (
            <div className="form-group">
              <label className="form-label">Spec / Notes</label>
              <input className="form-input" placeholder="Any spec details or notes"
                value={form.spec} onChange={e => set('spec', e.target.value)} />
            </div>
          ) : (
            <div className="form-hint" style={{ marginTop: 4 }}>
              Order text is generated automatically from the specifications above.
            </div>
          )}

            </div>
<div className="alu-modal-side">
            {/* PANEL DE PRECIO (7-ago). Columna sticky: el vendedor ve el
                monto MIENTRAS captura, no despues de grabar. Compacto a
                proposito para no robarle alto al dibujo. */}
            {calc && (
              <div style={{ background:'var(--bg-3)', border:'1px solid var(--border)',
                            borderRadius:8, padding:10, marginBottom:10, fontSize:12 }}>
                <div className="flex justify-between items-center" style={{ marginBottom:8, flexWrap:'wrap', gap:6 }}>
                  <span className="text-mono" style={{ color:'var(--text-3)' }}>{fmt.sqft(calc.sqft_unit*(form.qty||1))} sf</span>
                  <span>
                    <span className="text-muted">Rec </span>
                    <span className="text-mono" style={{fontWeight:700}}>{fmt.currency(calc.rec_sf)}</span>
                    {alertMeta && <span className={alertMeta.cls} style={{ marginLeft:6 }}>{alertMeta.label}</span>}
                  </span>
                </div>
                <div className="flex" style={{ gap:8, marginBottom:8 }}>
                  <div className="form-group" style={{ marginBottom:0, flex:2 }}>
                    <label className="form-label">Price $/sf</label>
                    <input className="form-input text-mono" type="number" step="0.01"
                      placeholder={calc ? fmt.number(calc.rec_sf) : '-'}
                      value={form.price_sf} onChange={e => { setPriceTouched(true); set('price_sf', e.target.value); }}
                      style={noPrice && form.model_code ? { borderColor: 'var(--orange)' } : undefined} />
                  </div>
                  <div className="form-group" style={{ marginBottom:0, flex:1 }}>
                    <label className="form-label">Disc %</label>
                    <input className="form-input text-mono" type="number" step="1" min="0"
                      max={isManager ? 100 : 5}
                      placeholder="0" value={form.disc_pct} onChange={e => set('disc_pct', e.target.value)} />
                  </div>
                </div>
                <div style={{ borderTop:'1px solid var(--border)', paddingTop:8,
                              display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                  <span style={{ fontWeight:600 }}>Net Sales</span>
                  <span className="text-mono" style={{ fontWeight:700, fontSize:17 }}>
                    {netSales() != null ? fmt.currency(netSales()) : '-'}
                  </span>
                </div>
                <div className="form-hint" style={{ marginTop:4 }}>Floor {fmt.currency(calc.floor_sf)}/sf · disc max 5%</div>
              </div>
            )}
            <AluElevationPreview form={{
              ...form,
              thickness_class: resolved.model ? resolved.model.thickness_class : '',
            }} />
              </div>          </div>
        </div>

        <div className="modal-footer flex justify-between items-center">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || saveBlocked}>
            {saving ? 'Saving…' : editData?.id ? 'Update Line' : 'Add Line'}
          </button>
        </div>

      </div>
    </div>
  );
}
